import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Check, Rocket, CreditCard, Gift, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { startCashfreePurchase } from "@/lib/cashfree";

export default function BoostDialog({ property, open, onOpenChange }) {
  const { user, refresh } = useAuth();
  const [tiers, setTiers] = useState([]);
  const [selected, setSelected] = useState("boost_15");
  const [paying, setPaying] = useState(false);
  const [claiming, setClaiming] = useState(false);

  const trialUsed = !!user?.boost_trial_used;
  const canTrial = !trialUsed && !property?.is_featured;

  useEffect(() => {
    if (!open) return;
    api.get("/payments/boost-packages")
      .then(({ data }) => setTiers(data))
      .catch(() => {});
  }, [open]);

  const startBoost = async () => {
    if (!property || !currentTier) return;
    setPaying(true);
    try {
      await startCashfreePurchase({ packageId: currentTier.id, propertyId: property.id });
    } catch (err) {
      toast.error(formatApiError(err));
      setPaying(false);
    }
  };

  const claimTrial = async () => {
    if (!property) return;
    setClaiming(true);
    try {
      await api.post("/payments/boost/trial", { property_id: property.id });
      await refresh();
      toast.success("🎉 Your free 7-day boost is live! Your listing is now featured.");
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setClaiming(false);
    }
  };

  const currentTier = tiers.find((t) => t.id === selected);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="boost-dialog" className="max-w-lg rounded-2xl">
        <DialogHeader>
          <div className="w-12 h-12 rounded-full bg-[#FFE9C7] flex items-center justify-center mb-2">
            <Rocket size={22} className="brand-forest" />
          </div>
          <DialogTitle className="font-display text-2xl brand-forest">
            {property?.is_featured ? "Extend boost duration" : "Boost this listing"}
          </DialogTitle>
          <DialogDescription>
            {property?.is_featured
              ? `Add more days on top of your active boost. "${property?.title}" will stay pinned at the top of search results.`
              : `Push "${property?.title}" to the top of search results. Boosted listings get up to 5× more views and inquiries.`}
          </DialogDescription>
        </DialogHeader>

        {/* Free trial banner */}
        {canTrial && (
          <button
            data-testid="claim-trial-boost"
            onClick={claimTrial}
            disabled={claiming}
            className="w-full text-left rounded-2xl border-2 border-dashed border-[#0EA5E9] bg-[#F0F9FF] p-4 flex items-center gap-3 hover:bg-[#E0F2FE] transition-colors disabled:opacity-60"
          >
            <div className="w-10 h-10 rounded-full bg-[#0EA5E9] text-white flex items-center justify-center shrink-0">
              <Gift size={18} />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-[#0369A1] text-sm">Try it free — 7-day boost on us</p>
              <p className="text-[11px] text-[#0EA5E9]">One-time free trial. No payment needed.</p>
            </div>
            <span className="text-[11px] font-bold text-white bg-[#0EA5E9] rounded-full px-3 py-1.5">
              {claiming ? "Activating…" : "Claim free"}
            </span>
          </button>
        )}

        {/* Tier selector */}
        <div className="grid grid-cols-3 gap-2 my-2">
          {tiers.map((t) => {
            const isSel = selected === t.id;
            const isBest = t.id === "boost_30";
            return (
              <button
                key={t.id}
                data-testid={`boost-tier-${t.id}`}
                onClick={() => setSelected(t.id)}
                className={`relative rounded-2xl p-4 border-2 text-left transition-colors ${
                  isSel ? "border-[#FF5A3D] bg-[#FFF1EB]" : "border-brand-subtle bg-white hover:border-[#FF5A3D]/40"
                }`}
              >
                {isBest && (
                  <span className="absolute -top-2 left-1/2 -translate-x-1/2 bg-[#0EA5E9] text-white text-[9px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full">Best value</span>
                )}
                <div className="font-display text-2xl brand-forest">{t.featured_days}d</div>
                <div className="font-display text-lg text-gray-900 mt-1">₹{t.amount}</div>
                <div className="text-[11px] text-gray-500 mt-1">
                  ₹{Math.round(t.amount / t.featured_days)}<span>/day</span>
                </div>
                {isSel && (
                  <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-[#FF5A3D] text-white flex items-center justify-center">
                    <Check size={12} />
                  </div>
                )}
              </button>
            );
          })}
        </div>

        <ul className="text-sm text-gray-600 space-y-1.5 my-3 bg-[#FFF6E9] rounded-xl p-4">
          <li className="flex items-center gap-2"><Check size={14} className="brand-forest" /> Top of search results in your city & locality</li>
          <li className="flex items-center gap-2"><Check size={14} className="brand-forest" /> "Featured" badge on your listing card</li>
          <li className="flex items-center gap-2"><Check size={14} className="brand-forest" /> Boost days stack if you renew early</li>
        </ul>

        <div className="flex items-center justify-between pt-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button>
          <Button
            data-testid="confirm-boost"
            onClick={startBoost}
            disabled={!currentTier || paying}
            className="bg-brand-forest hover:bg-[#E64520] text-white rounded-full h-11 px-6 font-semibold"
          >
            {paying ? <Loader2 size={16} className="mr-1 animate-spin" /> : <CreditCard size={16} className="mr-1" />}
            {!currentTier ? "Loading…" : paying ? "Opening checkout…" : `Pay ₹${currentTier.amount}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
